import { useEffect, useState } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu, X, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

export const ADMIN_SECTIONS = [
  { group: "Everyday" },
  { to: "/admin", label: "Overview", end: true, testid: "admin-nav-overview" },
  { to: "/admin/orders", label: "Orders", testid: "admin-nav-orders" },
  { to: "/admin/production", label: "Production & Printing", testid: "admin-nav-production" },
  { to: "/admin/products", label: "Products", testid: "admin-nav-products" },
  { to: "/admin/catalogue-review", label: "Catalogue Review", testid: "admin-nav-catalogue-review" },
  { to: "/admin/photos", label: "Photos", testid: "admin-nav-photos" },
  { to: "/admin/availability", label: "Prices & Availability", testid: "admin-nav-availability" },
  { to: "/admin/delivery", label: "Delivery & Collection", testid: "admin-nav-delivery" },
  { to: "/admin/content", label: "Website Content & Legal", testid: "admin-nav-content" },
  { to: "/admin/settings", label: "Business Settings", testid: "admin-nav-settings" },
  { to: "/admin/help", label: "Help & Setup", testid: "admin-nav-help" },
  { group: "Advanced" },
  { to: "/admin/emails", label: "Emails", testid: "admin-nav-emails" },
  { to: "/admin/newsletter", label: "Newsletter", testid: "admin-nav-newsletter" },
  { to: "/admin/account", label: "Account & Security", testid: "admin-nav-account" },
  { to: "/admin/advanced", label: "Advanced Settings", testid: "admin-nav-advanced" },
];

const NavItems = ({ onNavigate }) => (
  <>
    {ADMIN_SECTIONS.map((l) =>
      l.group ? (
        <p key={l.group} className="px-4 pb-1 pt-4 text-[10px] font-bold uppercase tracking-widest text-cream-muted/60">
          {l.group}
        </p>
      ) : (
        <NavLink
          key={l.to}
          to={l.to}
          end={l.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `block rounded-xl px-4 py-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus ${
              isActive ? "bg-gold text-choco" : "text-cream-muted hover:bg-choco hover:text-gold"
            }`
          }
          data-testid={l.testid}
        >
          {l.label}
        </NavLink>
      )
    )}
  </>
);

export const AdminLayout = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    api
      .get("/admin/auth/me")
      .then((res) => setMe(res.data))
      .catch(() => navigate("/admin/login"));
    // PWA: network-only service worker — nothing sensitive is ever cached
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw-admin.js", { scope: "/" }).catch(() => {});
    }
  }, [navigate]);

  const logout = async () => {
    await api.post("/admin/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!me) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-choco text-sm text-cream-muted" data-testid="admin-loading">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-choco" data-testid="admin-area">
      {/* Mobile top bar */}
      <header className="no-print sticky top-0 z-40 flex items-center justify-between border-b border-choco-line bg-choco-light px-4 py-3 lg:hidden">
        <button
          onClick={() => setDrawer(true)}
          className="rounded-xl border border-choco-line p-2.5 text-cream-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
          aria-label="Open menu"
          data-testid="admin-menu-button"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
        <img src="/assets/logo.png" alt="Bakes by CR" className="h-8 w-auto" />
        <button
          onClick={logout}
          className="rounded-xl border border-choco-line p-2.5 text-cream-muted hover:text-gold"
          aria-label="Sign out"
          data-testid="admin-logout-button-mobile"
        >
          <LogOut className="h-5 w-5" aria-hidden="true" />
        </button>
      </header>

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Admin menu" data-testid="admin-drawer">
          <div className="absolute inset-0 bg-choco/80" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] overflow-y-auto bg-choco-light p-4">
            <div className="flex items-center justify-between">
              <img src="/assets/logo.png" alt="Bakes by CR" className="h-9 w-auto" />
              <button
                onClick={() => setDrawer(false)}
                className="rounded-xl border border-choco-line p-2.5 text-cream-muted hover:text-gold"
                aria-label="Close menu"
                data-testid="admin-drawer-close"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <nav className="mt-5 space-y-1" aria-label="Admin sections">
              <NavItems onNavigate={() => setDrawer(false)} />
            </nav>
            <a href="/" target="_blank" rel="noreferrer" className="mt-4 flex items-center gap-2 rounded-xl border border-choco-line px-4 py-3 text-sm font-semibold text-cream-muted hover:text-gold" data-testid="admin-view-site-mobile">
              <ExternalLink className="h-4 w-4" aria-hidden="true" /> View live website
            </a>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="no-print sticky top-0 hidden h-screen w-60 shrink-0 flex-col overflow-y-auto border-r border-choco-line bg-choco-light p-4 lg:flex">
          <div className="flex items-center gap-3 px-2">
            <img src="/assets/logo.png" alt="Bakes by CR" className="h-10 w-auto" />
            <span className="text-xs font-semibold uppercase tracking-widest text-cream-muted">Admin</span>
          </div>
          <nav className="mt-6 flex-1 space-y-1" aria-label="Admin sections">
            <NavItems />
          </nav>
          <a href="/" target="_blank" rel="noreferrer" className="mb-2 flex items-center gap-2 rounded-xl border border-choco-line px-4 py-2.5 text-xs font-bold text-cream-muted hover:border-gold hover:text-gold" data-testid="admin-view-site">
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> View live website
          </a>
          <div className="flex items-center justify-between gap-2 border-t border-choco-line px-2 pt-3">
            <span className="truncate text-xs text-cream-muted" data-testid="admin-user-email">{me.email}</span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 rounded-full border border-choco-line px-3 py-1.5 text-xs font-bold text-cream-text transition-colors hover:border-gold hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Sign out
            </button>
          </div>
        </aside>

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <div className="mx-auto max-w-5xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
